import { useState, type FormEvent } from "react"

import {
  sandboxCustomerToCashRequestV1Schema,
  type CustomerStatementV1,
  type MembershipPersonaV1,
  type SandboxCustomerToCashRequestV1,
  type SandboxCustomerToCashResponseV1,
} from "@impetusos/contracts"

import {
  readCustomerStatement,
  runSandboxCustomerToCash,
} from "../transport/index.js"

const STORAGE_KEY = "impetusos.app003.sandbox-journey.v1"

type StoredJourney = {
  request: SandboxCustomerToCashRequestV1
  result: SandboxCustomerToCashResponseV1
}

const readStoredJourney = (): StoredJourney | null => {
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null")
    if (typeof value !== "object" || value === null) return null
    return {
      request: sandboxCustomerToCashRequestV1Schema.parse(value.request),
      result: value.result as SandboxCustomerToCashResponseV1,
    }
  } catch {
    return null
  }
}

const stageLabels: Record<string, string> = {
  customer: "Customer confirmed",
  estimate: "Estimate sent",
  approved_scope: "Scope approved",
  work_order_ready: "Work order ready",
  work_scheduled: "Work scheduled",
  work_completed: "Work completed",
  invoice: "Invoice finalized",
  stripe_checkout: "Stripe checkout ready",
}

const moneyFormatters = new Map<string, Intl.NumberFormat>()
const money = (amount: number, currency: string) => {
  const formatter = moneyFormatters.get(currency)
    ?? new Intl.NumberFormat("en-US", { style: "currency", currency })
  moneyFormatters.set(currency, formatter)
  return formatter.format(amount / 100)
}

export const CustomerJourneyWorkspace = ({
  persona,
  legalEntityId,
  branchId,
}: {
  persona: MembershipPersonaV1
  legalEntityId: string
  branchId: string
}) => {
  const [stored, setStored] = useState<StoredJourney | null>(readStoredJourney)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [statement, setStatement] = useState<CustomerStatementV1 | null>(null)
  const [draft, setDraft] = useState({
    displayName: "Harbor View Sandbox Customer",
    contactName: "Alex Sandbox",
    email: `app003-${Date.now()}@example.test`,
    addressLine1: "1400 Sandbox Avenue",
    city: "Richmond",
    region: "VA",
    postalCode: "23220",
  })

  const persist = (
    request: SandboxCustomerToCashRequestV1,
    result: SandboxCustomerToCashResponseV1,
  ) => {
    const next = { request, result }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    setStored(next)
  }

  const execute = async (
    action: SandboxCustomerToCashRequestV1["action"],
    seed?: SandboxCustomerToCashRequestV1,
  ) => {
    setBusy(true)
    setNotice(null)
    try {
      const request = sandboxCustomerToCashRequestV1Schema.parse({
        ...(seed ?? stored?.request ?? {
          requestVersion: 1,
          correlationId: crypto.randomUUID(),
          idempotencyKey: `app003-${crypto.randomUUID()}`,
          legalEntityId,
          branchId,
          customer: draft,
        }),
        action,
      })
      const result = await runSandboxCustomerToCash(request)
      persist(request, result)
      if (result.status === "scheduling_ready") {
        setNotice("The approved work order is ready. Switch to Dispatcher to schedule it.")
      } else if (result.status === "work_ready") {
        setNotice("Work is scheduled. Switch to the Worker sandbox identity and open Sandbox work.")
      } else if (result.status === "work_completed") {
        setNotice("Work and encrypted completion evidence are confirmed. Switch to Owner / administrator to prepare the invoice.")
      } else if (result.status === "checkout_ready") {
        setNotice("The finalized invoice is ready for Stripe test checkout.")
      } else {
        setNotice(`The ${result.failedStage?.replaceAll("_", " ") ?? "journey"} stage was not confirmed. No later stage was reported complete.`)
      }
    } catch {
      setNotice("The authoritative journey could not be confirmed. Review the current role and retry.")
    } finally {
      setBusy(false)
    }
  }

  const begin = (event: FormEvent) => {
    event.preventDefault()
    void execute("prepare")
  }

  const refreshStatement = async () => {
    if (stored?.result.customerId === null || stored === null) return
    setBusy(true)
    setNotice(null)
    try {
      const value = await readCustomerStatement({
        statementVersion: 1,
        customerId: stored.result.customerId,
        fromDate: "2026-01-01",
        toDate: "2026-12-31",
        cursor: 0,
        pageSize: 25,
      })
      setStatement(value)
      setNotice("Receipt and statement state refreshed from Frappe.")
    } catch {
      setNotice("The statement is not available for this role or the Stripe webhook is still processing.")
    } finally {
      setBusy(false)
    }
  }

  const reset = () => {
    window.localStorage.removeItem(STORAGE_KEY)
    setStored(null)
    setStatement(null)
    setNotice(null)
  }

  const status = stored?.result.status
  const completedStages = new Set(stored?.result.completedStages ?? [])
  const owner = persona === "administrator"
  const dispatcher = persona === "dispatcher"
  const worker = persona === "field_worker"

  return (
    <section className="journey-workspace" aria-labelledby="customer-journey-title">
      <div className="wizard-heading">
        <div>
          <p className="eyebrow">APP-003 sandbox journey</p>
          <h2 id="customer-journey-title">Customer to paid invoice</h2>
          <p className="muted">
            Every completed stage is confirmed by Frappe or Stripe test mode.
          </p>
        </div>
        <span className="step-badge">{persona.replaceAll("_", " ")}</span>
      </div>

      {notice && (
        <div className="notice" role="status">
          <span aria-hidden="true">!</span><p>{notice}</p>
        </div>
      )}

      {stored === null && owner && (
        <form className="journey-form" onSubmit={begin}>
          <label><span>Customer name</span><input required value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} /></label>
          <label><span>Contact name</span><input required value={draft.contactName} onChange={(event) => setDraft({ ...draft, contactName: event.target.value })} /></label>
          <label><span>Email</span><input required type="email" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} /></label>
          <label><span>Service address</span><input required value={draft.addressLine1} onChange={(event) => setDraft({ ...draft, addressLine1: event.target.value })} /></label>
          <label><span>City</span><input required value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} /></label>
          <label><span>State</span><input required value={draft.region} onChange={(event) => setDraft({ ...draft, region: event.target.value })} /></label>
          <label><span>Postal code</span><input required value={draft.postalCode} onChange={(event) => setDraft({ ...draft, postalCode: event.target.value })} /></label>
          <button className="primary-button" disabled={busy} type="submit">
            {busy ? "Confirming journey…" : "Create Customer and approved work"}
          </button>
        </form>
      )}

      {stored === null && !owner && (
        <p className="journey-role-message">
          Start this journey with the Owner / administrator sandbox identity.
        </p>
      )}

      {stored && (
        <>
          <ol className="journey-stages" aria-label="Confirmed journey stages">
            {Object.entries(stageLabels).map(([stage, label]) => (
              <li
                className={completedStages.has(
                  stage as SandboxCustomerToCashResponseV1["completedStages"][number],
                ) ? "complete" : ""}
                key={stage}
              >
                <span aria-hidden="true" />
                <div><strong>{label}</strong><small>{completedStages.has(
                  stage as SandboxCustomerToCashResponseV1["completedStages"][number],
                ) ? "Confirmed" : "Pending"}</small></div>
              </li>
            ))}
          </ol>

          <div className="journey-actions">
            {status === "scheduling_ready" && dispatcher && (
              <button className="primary-button" disabled={busy} type="button" onClick={() => { void execute("schedule_work") }}>
                {busy ? "Scheduling work…" : "Schedule approved work"}
              </button>
            )}
            {status === "scheduling_ready" && !dispatcher && (
              <p>Switch to Dispatcher, then open <strong>Sandbox work</strong> to schedule the approved work order.</p>
            )}
            {status === "work_ready" && worker && (
              <button className="primary-button" disabled={busy} type="button" onClick={() => { void execute("complete_work") }}>
                {busy ? "Recording work…" : "Complete sandbox work"}
              </button>
            )}
            {status === "work_ready" && !worker && (
              <p>Switch to the Worker sandbox identity, then open <strong>Sandbox work</strong>.</p>
            )}
            {status === "work_completed" && owner && (
              <button className="primary-button" disabled={busy} type="button" onClick={() => { void execute("prepare_checkout") }}>
                {busy ? "Finalizing invoice…" : "Finalize invoice and prepare payment"}
              </button>
            )}
            {status === "work_completed" && !owner && (
              <p>Switch to Owner / administrator to finalize the invoice.</p>
            )}
            {status === "checkout_ready" && stored.result.checkoutUrl && (
              <a className="primary-button" href={stored.result.checkoutUrl}>
                Pay {money(stored.result.amountMinor ?? 0, stored.result.currency ?? "USD")} in Stripe test mode
              </a>
            )}
            {status === "checkout_ready" && (
              <button className="secondary-button" disabled={busy} type="button" onClick={() => { void refreshStatement() }}>
                Refresh receipt and statement
              </button>
            )}
            <button className="text-button" type="button" onClick={reset}>Start another sandbox journey</button>
          </div>
        </>
      )}

      {statement && (
        <div className="journey-statement">
          <h3>Customer statement</h3>
          {statement.legalEntityGroups.map((group) => (
            <div key={group.legalEntityId}>
              <strong>{group.merchantName}</strong>
              <span>{money(group.paidMinor, group.currency)} paid · {money(group.balanceDueMinor, group.currency)} due</span>
              {group.invoices.map((invoice) => (
                <p key={invoice.invoiceId}>
                  Invoice {invoice.invoiceId.slice(0, 8)} · {invoice.state} · {money(invoice.totalMinor, group.currency)}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
